export type ArticleMeta = {
  title: string;
  description: string;
  navTitle: string;
  related: { slug: string; title: string }[];
};

type ArticleMetaConstraint<K extends string> = Omit<ArticleMeta, 'related'> & {
  related: { slug: K; title: string }[];
};

const defineArticles = <T extends Record<string, ArticleMetaConstraint<Extract<keyof T, string>>>>(
  articles: T,
) => articles;

export const articleMetadata = defineArticles({
  'what-is-a-gpx-file': {
    title: 'What is a GPX File? | GPX to KML Converter',
    description: 'Learn everything about GPX (GPS Exchange Format) files, what data they store, and how they are used for tracks, routes, and waypoints.',
    navTitle: 'What is a GPX file?',
    related: [
      { slug: 'what-is-a-kml-file', title: 'What is a KML file?' },
      { slug: 'how-to-convert-gpx-to-kml', title: 'How to convert GPX to KML' },
      { slug: 'gpx-files', title: 'Understanding GPX Files' }
    ]
  },
  'what-is-a-kml-file': {
    title: 'What is a KML File? | GPX to KML Converter',
    description: 'Discover the KML (Keyhole Markup Language) file format used by Google Earth. Learn how it stores placemarks, paths, and geographic data for visualization.',
    navTitle: 'What is a KML file?',
    related: [
      { slug: 'what-is-a-gpx-file', title: 'What is a GPX file?' },
      { slug: 'gpx-to-kml-google-earth', title: 'Open GPX in Google Earth' },
      { slug: 'kml-files', title: 'Understanding KML Files' }
    ]
  },
  'gpx-files': {
    title: 'Understanding GPX Files - Formats & Usage | GPX to KML Converter',
    description: 'A deep dive into the XML schema of GPX files. Understand waypoints, tracks, and routes for outdoor recreation and navigation.',
    navTitle: 'Understanding GPX Files',
    related: [
      { slug: 'kml-files', title: 'Understanding KML Files' },
      { slug: 'what-is-a-gpx-file', title: 'What is a GPX file?' }
    ]
  },
  'kml-files': {
    title: 'Understanding KML Files - Maps & Visualization | GPX to KML Converter',
    description: 'Explore the capabilities of KML files for representing geographic data, paths, and polygons in Google Earth and other mapping applications.',
    navTitle: 'Understanding KML Files',
    related: [
      { slug: 'gpx-files', title: 'Understanding GPX Files' },
      { slug: 'what-is-a-kml-file', title: 'What is a KML file?' }
    ]
  },
  'how-to-convert-gpx-to-kml': {
    title: 'How to Convert GPX to KML | Free Online Tutorial',
    description: 'Step-by-step guide to converting GPX files to KML format online for free. Learn how to transform GPS data for Google Earth visualization.',
    navTitle: 'How to convert GPX to KML',
    related: [
      { slug: 'gpx-to-kml-google-earth', title: 'Open GPX in Google Earth' },
      { slug: 'importing-gpx-into-mapsme-and-organicmaps', title: 'Import GPX to Maps.me & Organic Maps' }
    ]
  },
  'importing-gpx-into-mapsme-and-organicmaps': {
    title: 'Import GPX into Maps.me and Organic Maps | Tutorial',
    description: 'Learn how to import your GPS tracks and waypoints from a GPX file into Maps.me and Organic Maps for offline navigation on iOS and Android.',
    navTitle: 'Import GPX to Maps.me & Organic Maps',
    related: [
      { slug: 'how-to-convert-gpx-to-kml', title: 'How to Convert GPX to KML' },
      { slug: 'what-is-a-kml-file', title: 'What is a KML file?' }
    ]
  },
  'gpx-to-kml-google-earth': {
    title: 'How to Open GPX in Google Earth | GPX to KML Converter',
    description: 'Easily view your GPX files in 3D using Google Earth. Convert GPX to KML to visualize your hikes, rides, and routes on satellite imagery.',
    navTitle: 'Open GPX in Google Earth',
    related: [
      { slug: 'how-to-convert-gpx-to-kml', title: 'How to Convert GPX to KML' },
      { slug: 'what-is-a-gpx-file', title: 'What is a GPX file?' },
    ]
  }
});

export type ArticleSlug = keyof typeof articleMetadata;

export type ArticleNavGroup = {
  heading: string;
  slugs: ArticleSlug[];
};

export const isKnownSlug = (slug: string | undefined): slug is ArticleSlug =>
  !!slug && slug in articleMetadata;

export const articleNavGroups: ArticleNavGroup[] = [
  {
    heading: 'Guides & Tutorials',
    slugs: [
      'how-to-convert-gpx-to-kml',
      'gpx-to-kml-google-earth',
      'importing-gpx-into-mapsme-and-organicmaps',
    ],
  },
  {
    heading: 'Understanding Data',
    slugs: [
      'what-is-a-gpx-file',
      'what-is-a-kml-file',
      'gpx-files',
      'kml-files',
    ],
  },
];
